/**
 * The frontend's reference entries.
 *
 * The reference registry (which chip a reference type renders, how it searches)
 * lives in @auger/ui (fields/reference/registry), with the entries whose chips
 * the package owns: contact, socials, time, user, tagging, record, backlink.
 * This file registers the rest, and the `search` functions, since they read the
 * app's data: its Apollo client, `getAuger`, and the users store; and a filtered
 * field's scope (its filter note and the links outside its filter), which reads
 * the types store and the client. It also re-exports the registry's API, so
 * callers keep importing from here.
 *
 * Adding a new reference type means adding one entry here (or in the package, if
 * its chip and search need nothing from the app).
 */
import { hasActiveConditions } from "@auger/core/fields"
import { searchTags } from "@auger/core/gql"
import {
  type ReferenceEntity,
  registerReferenceEntries,
  registerReferenceScope,
} from "@auger/ui/fields/reference/registry"
import FileElement from "@/components/elements/FileElement.vue"
import SourceElement from "@/components/elements/SourceElement.vue"
import TypeElement from "@/components/elements/TypeElement.vue"
import { useLinksOutsideFilter } from "@/composables/useLinksOutsideFilter"
import { client } from "@/init/apollo"
import { getAuger } from "@/init/auger"
import { getType } from "@/stores/types"
import { useUsersStore } from "@/stores/users"

export {
  REFERENCE_REGISTRY,
  type ReferenceEntity,
  referenceEntry,
} from "@auger/ui/fields/reference/registry"

registerReferenceScope({
  filterNote: (field) => {
    if (!field?.recordType || !hasActiveConditions(field.filter)) return null
    const plural = getType(field.recordType)?.plural_label?.toLowerCase() || `${field.recordType}s`
    return `Only ${plural} matching this field's filter`
  },
  linksOutsideFilter: useLinksOutsideFilter,
})

registerReferenceEntries({
  // Display-only: a record's type is fixed at creation (changing it would
  // invalidate schema/fields), so the grid renders read-only TypeElement chips
  // with no picker; the `type` FieldType's form/filter use ReferenceSelect.
  type: {
    element: TypeElement,
  },
  // The chip is the package's UserElement; the search reads the staff directory.
  user: {
    search: (q) => {
      const lower = q.toLowerCase()
      return Promise.resolve(
        (useUsersStore().users as ReferenceEntity[]).filter(
          (u) =>
            (u.name as string | undefined)?.toLowerCase().includes(lower) ||
            (u.email as string | undefined)?.toLowerCase().includes(lower)
        )
      )
    },
  },
  record: {
    // No type searches every record type, up to 100; a typed search takes the
    // query's default of 20, inside the field's filter when it has one.
    search: (q, opts) =>
      getAuger(client)
        .records.search(`%${q}%`, {
          type: opts.recordType,
          limit: opts.recordType ? undefined : 100,
          filter: opts.filter,
        })
        .then((found) => found as ReferenceEntity[]),
  },
  // Display-only: sources are managed through their own ingestion / linking
  // flows, not an inline picker, so this entry registers just the chip element
  // (no `search` → ReferenceField renders read-only source chips, no picker).
  source: {
    element: SourceElement,
  },
  // Junctions are references whose displayed entities are the inner reference's
  // targets: tagging → tag, attachment → asset. link/backlink target records, so
  // they fall back to the `record` entry. `tagging` searches EXISTING tags (no
  // find-or-create yet — an unmatched name has nowhere to land inline).
  // Row persistence is `@auger/core`'s (records.update reconciles the emitted
  // target set); backlink stays display-only in the grid.
  tagging: {
    search: (q) =>
      client
        .query({ query: searchTags, variables: { search: `%${q}%` }, fetchPolicy: "network-only" })
        .then(({ data }) => (data?.tags ?? []) as ReferenceEntity[]),
  },
  // Searches the file library by name or path; the picked asset row carries
  // mime_type, which the write needs for record_attachments.asset_type.
  attachment: {
    element: FileElement,
    placeholder: "Add file...",
    search: (q) =>
      getAuger(client)
        .assets.search({ text: q, limit: 20 })
        .then((page) => page.items as ReferenceEntity[]),
  },
})
